import { useState, type CSSProperties, type ReactElement, type ReactNode } from "react";
import { AnimatePresence, motion, useIsPresent } from "framer-motion";
import {
    color,
    swapLayerStyle,
    useHydrationCrossfade,
    type HydrationCrossfade,
} from "@tokens/index";
import { motionStyle } from "./motionStyle";

/**
 * ContentSwap: the skeleton ↔ content swap under the entrance policy
 * (`useHydrationCrossfade` in `tokens/motion-react`).
 *
 * The two layers share one positioned host. The layer that arrives fades in;
 * the layer that leaves drops out of the flow (`position: absolute; top/left/right: 0`)
 * and fades out over it, keeping its intrinsic height so a taller or shorter
 * outgoing skeleton is not stretched. The content owns the layout from its
 * first frame and the swap reads as one crossfade, not as a per-row entrance.
 * Content on hand at mount (a cache hit) renders with no motion at all.
 *
 * Each layer is opaque (painted in the host's `surface` colour) and keeps its
 * compositor layer for its lifetime (`swapLayerStyle`), so the text inside it
 * rasterizes once, the same way as the page, and the end of the fade does not
 * repaint it.
 *
 * Put the list's own `<AnimatePresence initial={false}>` inside `children`;
 * rows that join the list later still run their own arrival.
 */
export type ContentSwapSurface = "canvas" | "surface" | "surface2";

export interface ContentSwapProps {
    /** `true` once the content is on hand. `false` shows `skeleton`. */
    readonly ready: boolean;
    readonly skeleton: ReactNode;
    readonly children: ReactNode;
    /**
     * Background of the host the swap sits on. The layers paint it, so the
     * crossfade is opaque and the text keeps subpixel rendering. Defaults to
     * `canvas` (the page). Pass `surface` inside a dialog or a surface pane.
     */
    readonly surface?: ContentSwapSurface | undefined;
    /** Style of the host box. Forward flex sizing here when the host must fill its parent. */
    readonly style?: CSSProperties;
    /** Style both layers share, for example the column layout the rows expect. */
    readonly layerStyle?: CSSProperties;
}

interface LayerProps {
    readonly fade: HydrationCrossfade;
    readonly surface: ContentSwapSurface;
    readonly layerStyle: CSSProperties | undefined;
    readonly children: ReactNode;
}

function Layer({ fade, surface, layerStyle, children }: LayerProps): ReactElement {
    const isPresent = useIsPresent();
    return (
        <motion.div
            aria-hidden={isPresent ? undefined : true}
            initial={fade.enter.initial}
            animate={fade.enter.animate}
            exit={fade.exit}
            transition={fade.enter.transition}
            style={motionStyle({
                minWidth: 0,
                ...swapLayerStyle(color.bg[surface]),
                ...layerStyle,
                ...(isPresent
                    ? {}
                    : {
                          position: "absolute",
                          top: 0,
                          left: 0,
                          right: 0,
                          pointerEvents: "none",
                      }),
            })}
        >
            {children}
        </motion.div>
    );
}

export function ContentSwap({
    ready,
    skeleton,
    children,
    surface = "canvas",
    style,
    layerStyle,
}: ContentSwapProps): ReactElement {
    const fade = useHydrationCrossfade();
    // Clip only while a layer leaves, so the absolute layer cannot paint past
    // the host and a focus ring is never cut once the swap has settled.
    const [swapping, setSwapping] = useState(false);
    const [lastReady, setLastReady] = useState(ready);
    if (ready !== lastReady) {
        setLastReady(ready);
        setSwapping(true);
    }
    return (
        <div
            data-content-swap={ready ? "content" : "skeleton"}
            style={{
                position: "relative",
                minWidth: 0,
                overflow: swapping ? "hidden" : undefined,
                ...style,
            }}
        >
            <AnimatePresence initial={false} onExitComplete={() => setSwapping(false)}>
                {ready ? (
                    <Layer key="content" fade={fade} surface={surface} layerStyle={layerStyle}>
                        {children}
                    </Layer>
                ) : (
                    <Layer key="skeleton" fade={fade} surface={surface} layerStyle={layerStyle}>
                        {skeleton}
                    </Layer>
                )}
            </AnimatePresence>
        </div>
    );
}
